// ─── Grille horaire (vues Semaine / Jour) — échelle variable ─────
// Colonne d'heures + N colonnes de jours. Les heures DENSES sont dilatées
// (plus hautes) pour lire les événements serrés sans chevauchement — calcul
// dans services/grilleHoraire (module pur, partagé). Les événements sans heure
// ("toute la journée") sont regroupés en pastilles en tête de colonne.
import React, { useMemo, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { useUI, useT, frJM } from '@mop/core';
import { parseHeureMin } from './services/affichageConfig';
import { construireGrille, layoutChevauchement } from './services/grilleHoraire';
import { emojiCategorie } from './data/categories';
import { GARDE_COL_DEFAUT } from './services/garde';

// Icône d'un événement : 🔁 récurrent, 🗓️ plage, sinon l'emoji de sa catégorie.
const iconeEvt = (ev) => (ev.isRecurrent ? '🔁' : ev.estPlage ? '🗓️' : (emojiCategorie(ev.categorie) || '📌'));

const HEURE_COL_W = 42;
const GARDE_COL = GARDE_COL_DEFAUT;   // source unique : services/garde

export default function GrilleHoraire({ jours, map, cfg, garde = {}, gardeCol = GARDE_COL, excCol = {}, evtIcone = true, dkDe, jourSel, onTapJour, onLongJour, onTapEvent, onLongCreneau, avecEntete = true }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const pressY = useRef(0);   // dernière position verticale d'appui (→ heure via G.pxToMin)

  const JOURS = t('commun.joursCourts');
  const auj = new Date();
  const estAuj = (d) => d.getDate() === auj.getDate() && d.getMonth() === auj.getMonth() && d.getFullYear() === auj.getFullYear();

  // Par jour : plages multi-jours (blocs couvrants) + « toute la journée » + chronométrés.
  const parJour = useMemo(() => jours.map(d => {
    const dk = dkDe(d);
    const evs = map[dk] || [];
    const plage = evs.filter(e => e.estPlage);
    const allday = evs.filter(e => !e.estPlage && parseHeureMin(e.heure) == null);
    const timed = evs.filter(e => !e.estPlage && parseHeureMin(e.heure) != null);
    return { d, dk, allday, timed, plage };
  }), [jours, map, dkDe]);
  const aAllDay = parJour.some(j => j.allday.length > 0);

  // Grille à échelle VARIABLE, commune aux colonnes (rangées alignées).
  // `horsPlage` (config) : true → la grille s'ÉTEND pour montrer les événements
  // (et bornes de plages multi-jours) hors de la plage d'heures ; false → stricte.
  const bornesPlages = useMemo(() => parJour.flatMap(j => j.plage.flatMap(p => {
    const out = [];
    if (p.plageRole === 'debut') { const m = parseHeureMin(p.plageHeureDebut); if (m != null) out.push(m); }
    if (p.plageRole === 'fin')   { const m = parseHeureMin(p.plageHeureFin);   if (m != null) out.push(m); }
    return out;
  })), [parJour]);
  const G = useMemo(() => construireGrille(
    parJour.flatMap(j => j.timed),
    { plageDebut: cfg.heureDebut * 60, plageFin: cfg.heureFin * 60, pasDefaut: cfg.pasMinutes || 60, row: cfg.hauteurHeure, dureeDefaut: cfg.dureeDefaut,
      etendre: cfg.horsPlage !== false, bornesSupp: bornesPlages },
  ), [parJour, cfg, bornesPlages]);

  const horsPlageHaut = G.start < G.plageDebut ? G.minToPx(G.plageDebut) : 0;
  const horsPlageBas = G.end > G.plageFin ? G.hauteur - G.minToPx(G.plageFin) : 0;

  // Scroll auto sur l'heure actuelle à l'ouverture — seulement quand la
  // PÉRIODE affichée change (navigation jour/semaine), jamais sur un
  // re-rendu quelconque (zoom, bascule icône/titre…) qui écraserait le
  // défilement manuel de l'utilisateur.
  const scrollRef = useRef(null);
  const clefPeriode = parJour.length ? `${parJour[0].dk}_${parJour[parJour.length - 1].dk}` : '';
  useEffect(() => {
    const nowMin = auj.getHours() * 60 + auj.getMinutes();
    if (parJour.some(j => estAuj(j.d)) && nowMin >= G.start && nowMin <= G.end) {
      scrollRef.current?.scrollTo({ y: Math.max(0, G.minToPx(nowMin) - 120), animated: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clefPeriode]);

  return (
    <View style={s.wrap}>
      {avecEntete && (
        <View style={[s.entete, { paddingLeft: HEURE_COL_W }]}>
          {parJour.map(({ d, dk }, i) => {
            const sel = jourSel && dkDe(jourSel) === dk;
            const gcol = garde[dk] ? gardeCol[garde[dk]] : null;
            const ecol = excCol[dk];                                   // échange de garde → anneau couleur du template
            const ringCol = ecol;                                      // anneau du numéro réservé aux échanges
            return (
              <TouchableOpacity key={i} style={[s.jourTete, sel && s.jourTeteSel]} onPress={() => onTapJour?.(d)}
                onLongPress={onLongJour ? () => onLongJour(d) : undefined} delayLongPress={350}>
                <Text style={[s.jourNom, estAuj(d) && s.jourNomAuj]}>{JOURS[(d.getDay() + 6) % 7]}</Text>
                <View style={[s.jourNum, estAuj(d) && s.jourNumAuj, gcol && { backgroundColor: gcol + '30' }, ringCol && { borderWidth: 2, borderColor: ringCol }]}>
                  <Text style={[s.jourNumTxt, estAuj(d) && { color: '#fff' }, gcol && !estAuj(d) && { color: gcol }]}>{d.getDate()}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      )}

      {aAllDay && (
        <View style={[s.allDayRow, { paddingLeft: HEURE_COL_W }]}>
          {parJour.map(({ allday, dk }, i) => (
            <View key={i} style={s.allDayCol}>
              {allday.slice(0, 3).map(ev => (
                <TouchableOpacity key={ev.id} style={[s.allDayEv, evtIcone && s.allDayEvIc, ev._couleur && { backgroundColor: ev._couleur }]} onPress={() => onTapEvent?.(ev, dk)}>
                  <Text style={s.allDayEvTxt} numberOfLines={1}>{evtIcone ? iconeEvt(ev) : `${ev.isRecurrent ? '🔁 ' : ev.estPlage ? '🗓️ ' : ''}${ev.titre}`}</Text>
                </TouchableOpacity>
              ))}
              {allday.length > 3 && <Text style={s.allDayPlus}>+{allday.length - 3}</Text>}
            </View>
          ))}
        </View>
      )}

      <ScrollView ref={scrollRef} style={s.scroll} contentContainerStyle={{ paddingTop: 10, paddingBottom: 40 }} nestedScrollEnabled showsVerticalScrollIndicator={false}>
        <View style={{ flexDirection: 'row', height: G.hauteur }}>
          {/* Colonne heures. */}
          <View style={[s.heuresCol, { width: HEURE_COL_W, height: G.hauteur }]}>
            {horsPlageHaut > 0 && <View style={[s.horsPlage, { top: 0, height: horsPlageHaut }]} />}
            {horsPlageBas > 0 && <View style={[s.horsPlage, { top: G.hauteur - horsPlageBas, height: horsPlageBas }]} />}
            {G.lignes.filter(l => l.label).map((l, li) => (
              <Text key={li} style={[s.heureTxt, !l.heure && s.heureTxtSub, { top: l.top - 7 }]}>{l.label}</Text>
            ))}
          </View>
          {/* Colonnes jours. */}
          {parJour.map(({ d, dk, timed, plage }, ci) => {
            const gcol = garde[dk] ? gardeCol[garde[dk]] : null;
            const sel = jourSel && dkDe(jourSel) === dk;
            // Plages multi-jours transformées en événements synthétiques bornés à la
            // grille → UN SEUL layout en colonnes avec les événements chronométrés :
            // un événement qui chevauche une plage s'affiche À CÔTÉ (style Outlook),
            // plus jamais par-dessus.
            const hm = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
            const evPlages = plage.map(p => {
              const md = parseHeureMin(p.plageHeureDebut); const mf = parseHeureMin(p.plageHeureFin);
              const topMin = p.plageRole === 'debut' ? (md != null ? Math.max(md, G.start) : G.start) : G.start;
              const botMin = p.plageRole === 'fin' ? (mf != null ? Math.min(mf, G.end) : G.end) : G.end;
              return { ...p, _plage: true, heure: hm(topMin), heureFin: hm(Math.max(botMin, topMin + 10)) };
            });
            const blocs = layoutChevauchement([...evPlages, ...timed], G, cfg.dureeDefaut);
            const nowMin = auj.getHours() * 60 + auj.getMinutes();
            return (
              <TouchableOpacity key={ci} activeOpacity={0.9} onPress={() => onTapJour?.(d)}
                onPressIn={(e) => { pressY.current = e.nativeEvent.locationY; }}
                onLongPress={onLongCreneau ? () => onLongCreneau(d, G.pxToMin(pressY.current)) : undefined} delayLongPress={320}
                style={[s.jourCol, { height: G.hauteur }, gcol && { backgroundColor: gcol + '12' }, sel && s.jourColSel]}>
                {/* Sélection du jour : rails latéraux accent (n'imite aucun bloc
                    d'événement, contrairement à un fond translucide bleu). */}
                {sel && <View pointerEvents="none" style={s.selRails} />}
                {horsPlageHaut > 0 && <View style={[s.horsPlage, { top: 0, height: horsPlageHaut }]} />}
                {horsPlageBas > 0 && <View style={[s.horsPlage, { top: G.hauteur - horsPlageBas, height: horsPlageBas }]} />}
                {G.lignes.map((l, li) => (
                  <View key={li} style={[s.ligneH, !l.heure && s.ligneHSub, { top: l.top }]} />
                ))}
                {estAuj(d) && nowMin >= G.start && nowMin <= G.end && (
                  <View style={[s.maintenant, { top: G.minToPx(nowMin) }]} />
                )}
                {blocs.map(b => {
                  const largeur = `${100 / b.nCols}%`;
                  const gauche = `${(100 / b.nCols) * b.col}%`;
                  // Plage multi-jours : même layout en colonnes que les événements
                  // (côte à côte, style Outlook), rendu translucide à bord coloré.
                  if (b.ev._plage) {
                    const p = b.ev;
                    const c = p._couleur || gcol || UI.accent;
                    // Début et fin TOUJOURS affichés, empilés en HAUT du bloc.
                    // Si le début (ou la fin) est sur un AUTRE jour → date courte
                    // JJ/MM devant l'heure (au lieu du « … » d'avant).
                    const debTxt = p.plageRole === 'debut'
                      ? (p.plageHeureDebut || '')
                      : `${frJM(p.plageSourceDk)}${p.plageHeureDebut ? ` ${p.plageHeureDebut}` : ''}`;
                    const finTxt = p.plageRole === 'fin'
                      ? (p.plageHeureFin || '')
                      : `${frJM(p.dateFin)}${p.plageHeureFin ? ` ${p.plageHeureFin}` : ''}`;
                    const ic = p.isRecurrent ? '🔁' : (emojiCategorie(p.categorie) || '🗓️');
                    return (
                      <TouchableOpacity key={`pl-${p.id}`} onPress={() => onTapEvent?.(p, dk)}
                        style={[s.plageBloc, { top: b.topPx, height: Math.max(10, b.hPx), left: gauche, width: largeur, backgroundColor: c + '33', borderColor: c }]}>
                        <View style={s.plageHeures}>
                          {!!debTxt && <Text style={[s.plageHeure, { color: c }]} numberOfLines={1}>{debTxt}</Text>}
                          {!!finTxt && <Text style={[s.plageHeure, { color: c }]} numberOfLines={1}>{finTxt}</Text>}
                        </View>
                        {evtIcone
                          ? <Text style={s.plageIcone} numberOfLines={1}>{ic}</Text>
                          : <Text style={[s.plageTxt, { color: c }]} numberOfLines={2}>{p.titre}</Text>}
                        <View />
                      </TouchableOpacity>
                    );
                  }
                  // Police et icône dimensionnées d'après la HAUTEUR réelle du bloc
                  // (donc suivent le zoom au pincement et la place disponible).
                  const hSize = Math.max(7, Math.min(12, Math.round(b.hPx * 0.16)));
                  const tSize = Math.max(9, Math.min(14, Math.round(b.hPx * 0.20)));
                  const icSize = Math.max(11, Math.min(28, Math.round(b.hPx * 0.42)));
                  return (
                    <TouchableOpacity key={b.ev.id} onPress={() => onTapEvent?.(b.ev, dk)}
                      style={[s.bloc, s.blocIco, { top: b.topPx, height: Math.max(14, b.hPx - 2), left: gauche, width: largeur, backgroundColor: (b.ev._couleur || gcol || UI.accent) }]}>
                      {/* Début en haut-gauche · icône OU titre centré · fin en bas-droite. */}
                      <Text style={[s.blocHeureIc, { fontSize: hSize, lineHeight: hSize + 2, alignSelf: 'flex-start' }]} numberOfLines={1}>{b.ev.heure}</Text>
                      {evtIcone
                        ? <Text style={[s.blocIcone, { fontSize: icSize, lineHeight: icSize + 1 }]} numberOfLines={1}>{iconeEvt(b.ev)}</Text>
                        : <Text style={[s.blocTitre, { fontSize: tSize, textAlign: 'center', alignSelf: 'stretch' }]} numberOfLines={2}>{b.ev.isRecurrent ? '🔁 ' : ''}{b.ev.titre}</Text>}
                      <Text style={[s.blocHeureIc, s.blocHeureFin, { fontSize: hSize, lineHeight: hSize + 2, alignSelf: 'flex-end' }]} numberOfLines={1}>{b.ev.heureFin || ''}</Text>
                    </TouchableOpacity>
                  );
                })}
              </TouchableOpacity>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  wrap: { flex: 1 },
  entete: { flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: UI.border, paddingBottom: 4 },
  jourTete: { flex: 1, alignItems: 'center', paddingVertical: 3, borderRadius: 8 },
  jourTeteSel: { backgroundColor: UI.accentTint, borderWidth: 2, borderColor: UI.accent, borderRadius: 12 },
  jourNom: { fontSize: 10.5, fontWeight: '700', color: UI.faint },
  jourNomAuj: { color: UI.accent },
  jourNum: { width: 24, height: 24, borderRadius: 12, alignItems: 'center', justifyContent: 'center', marginTop: 2 },
  jourNumAuj: { backgroundColor: UI.accent },
  jourNumTxt: { fontSize: 12.5, fontWeight: '700', color: UI.ink },
  allDayRow: { flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: UI.border, paddingVertical: 3 },
  allDayCol: { flex: 1, paddingHorizontal: 1, gap: 2 },
  allDayEv: { backgroundColor: UI.accent, borderRadius: 5, paddingHorizontal: 4, paddingVertical: 2 },
  allDayEvIc: { alignItems: 'center', paddingHorizontal: 2 },
  allDayEvTxt: { fontSize: 9.5, color: '#fff', fontWeight: '600' },
  allDayPlus: { fontSize: 9, color: UI.faint, textAlign: 'center' },
  scroll: { flex: 1 },
  heuresCol: { position: 'relative' },
  heureTxt: { position: 'absolute', right: 5, fontSize: 10.5, color: UI.faint, fontWeight: '700' },
  heureTxtSub: { fontSize: 9, color: UI.faint, fontWeight: '500' },
  jourCol: { flex: 1, borderLeftWidth: 0.5, borderLeftColor: UI.border, position: 'relative' },
  jourColSel: { backgroundColor: UI.selFond },
  selRails: { position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, borderLeftWidth: 2, borderRightWidth: 2, borderColor: UI.accent, zIndex: 1 },
  horsPlage: { position: 'absolute', left: 0, right: 0, backgroundColor: 'rgba(255,167,38,0.07)' },
  ligneH: { position: 'absolute', left: 0, right: 0, height: 0.5, backgroundColor: UI.border },
  ligneHSub: { backgroundColor: UI.border, opacity: 0.4 },
  maintenant: { position: 'absolute', left: 0, right: 0, height: 1.5, backgroundColor: '#E5397B', zIndex: 5 },
  bloc: { position: 'absolute', borderRadius: 6, paddingHorizontal: 4, paddingVertical: 1, overflow: 'hidden', borderWidth: 0.5, borderColor: 'rgba(255,255,255,0.5)' },
  blocIco: { alignItems: 'center', justifyContent: 'space-between', paddingVertical: 0 },   // heure haut · icône centrée · heure fin bas
  blocHeure: { fontSize: 9, color: 'rgba(255,255,255,0.9)', fontWeight: '700' },
  blocHeureFin: { color: 'rgba(255,255,255,0.75)' },
  blocTitre: { fontSize: 10.5, color: '#fff', fontWeight: '700' },
  // Mode icône : tailles réduites + interlignes serrés pour tenir dans un créneau court.
  blocHeureIc: { fontSize: 7.5, lineHeight: 9, color: 'rgba(255,255,255,0.9)', fontWeight: '700' },
  blocIcone: { fontSize: 12, lineHeight: 14 },
  // Plage multi-jours (style Outlook) : bloc couvrant, translucide, bord gauche coloré.
  plageBloc: { position: 'absolute', borderRadius: 6, borderLeftWidth: 3, paddingHorizontal: 5, paddingVertical: 2, overflow: 'hidden', alignItems: 'center', justifyContent: 'space-between' },
  plageTxt: { fontSize: 10.5, fontWeight: '800', textAlign: 'center' },
  plageIcone: { fontSize: 14 },
  plageHeure: { fontSize: 9, fontWeight: '800', opacity: 0.9 },
  plageHeures: { alignItems: 'center' },
});
